import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phone-input-override.css';
import LegalModal from '../components/LegalModal';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import { mvpApi, getGoogleAuthUrl, getAppleAuthUrl } from '../services/api';
import { getStoredPartnerToken, getStoredReferralCode, setStoredPartnerToken, getPathWithRef } from '../utils/attribution';
import { markApplicantSessionStarted } from '../utils/applicantSession';
import './Signup.css';

const APPLICANT_ENTRY_INTENT_KEY = 'mvp_applicant_entry_intent';

const STEPS = { MOBILE: 1, CHANNEL: 2, OTP: 3 };
const PHONE_LENGTH_RULES = {
  34: { min: 9, max: 9 }, // Spain
  91: { min: 10, max: 10 }, // India
  86: { min: 11, max: 11 }, // China
  52: { min: 10, max: 10 }, // Mexico
  55: { min: 11, max: 11 }, // Brazil
  54: { min: 10, max: 11 }, // Argentina
  57: { min: 10, max: 10 }, // Colombia
  51: { min: 9, max: 9 }, // Peru
  56: { min: 9, max: 9 }, // Chile
  971: { min: 9, max: 9 }, // UAE
  966: { min: 9, max: 9 }, // Saudi Arabia
  974: { min: 8, max: 8 }, // Qatar
  965: { min: 8, max: 8 }, // Kuwait
  972: { min: 9, max: 9 }, // Israel
  962: { min: 9, max: 9 }, // Jordan
  44: { min: 10, max: 10 }, // UK
  33: { min: 9, max: 9 }, // France
  49: { min: 10, max: 11 }, // Germany
  39: { min: 9, max: 10 }, // Italy
  61: { min: 9, max: 10 }, // Australia
  212: { min: 9, max: 9 }, // Morocco
  1: { min: 10, max: 10 }, // USA
};
const PHONE_GROUP_RULES = {
  // Display formatting only (stored value remains digits-only)
  // Keys are dial codes as strings.
  '34': [3, 3, 3], // Spain: 612 345 678
  '91': [5, 5], // India: 98765 43210
  '1': [3, 3, 4], // United States: (415) 555-2671 (handled specially below)
  '44': [5, 3, 3], // United Kingdom: 07400 123 456
  '33': [2, 2, 2, 2, 2], // France: 06 12 34 56 78
  '49': [4], // Germany: 01512 3456789 (4 + rest)
  '39': [3, 3, 4], // Italy: 312 345 6789
  '55': [2, 5, 4], // Brazil: (21) 98765-4321 (handled specially below)
  '61': [4, 3, 3], // Australia: 0412 345 678
};
const MIDDLE_EAST_DIAL_CODES = new Set(['971', '966', '974', '965', '972', '962']);
const LATAM_DIAL_CODES = new Set(['52', '55', '54', '57', '51', '56']);

/** Google G logo for social button (from mockup) */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z" fill="#FFC107" />
      <path d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" fill="#FF3D00" />
      <path d="M24 44c5.4 0 10.3-2 14-5.2l-6.5-5.5C29.5 35.1 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8H6.3C9.6 35.6 16.2 44 24 44z" fill="#4CAF50" />
      <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C37.4 38.5 44 33.5 44 24c0-1.3-.1-2.7-.4-3.9z" fill="#1976D2" />
    </svg>
  );
}

/** Apple logo for social button (from mockup) */
function AppleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 814 1000" fill="currentColor" aria-hidden>
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.8 135.4-317.5 269-317.5 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 188.3-49.1 30.3 0 130.3 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

/** Lightbulb icon for channel tip card */
function LightbulbIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14" />
      <path d="M12 2v2M4.93 4.93l1.41 1.41M2 12h2M4.93 19.07l1.41-1.41M12 20v-2M19.07 19.07l-1.41-1.41M22 12h-2M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

/** Returns a user-friendly message for an SSO error code, or a generic fallback. */
function getSsoErrorMessage(code, t) {
  if (!code || typeof code !== 'string') return null;
  const key = code.trim().toLowerCase();
  const mapped = {
    email_already_registered: t('signup.errors.emailExists'),
    access_denied: t('signup.errors.accessDenied'),
    session_failed: t('signup.errors.sessionFailed'),
    token_exchange_failed: t('signup.errors.tokenExchangeFailed'),
    user_not_registered: t('signup.errors.userNotRegistered'),
    invalid_request: t('signup.errors.invalidRequest'),
    server_error: t('signup.errors.serverError'),
  };
  return mapped[key] || t('signup.errors.ssoGeneric');
}

function normalizeApiError(err, fallbackKey, t) {
  const errorMessage = typeof err?.message === 'string' ? err.message.toLowerCase() : '';
  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || errorMessage.includes('timeout')) {
    return t('signup.errors.requestTimeout');
  }

  const raw = err?.response?.data?.detail;
  const detail = typeof raw === 'string'
    ? raw.trim().toLowerCase()
    : Array.isArray(raw)
      ? String(raw[0]?.msg || '').trim().toLowerCase()
      : '';

  const known = {
    'this number is already registered. please sign in.': t('signup.errors.numberAlreadyRegistered'),
  };

  return known[detail] || (typeof raw === 'string' ? raw : null) || err?.message || t(fallbackKey);
}

export default function Signup() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const refParam = searchParams.get('ref') || '';
  const navigate = useNavigate();
  const goToThankYouForExistingUser = () => {
    navigate('/welcome', { replace: true });
  };

  const [step, setStep] = useState(STEPS.MOBILE);
  const [countryCode, setCountryCode] = useState('34');
  const [nationalNumber, setNationalNumber] = useState('');
  // Default sms while WhatsApp channel card is commented out below
  const [channel, setChannel] = useState('sms');
  const [otpCode, setOtpCode] = useState('');
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ssoError, setSsoError] = useState(null);
  const [phoneValidationError, setPhoneValidationError] = useState(false);
  const [openLegal, setOpenLegal] = useState(null);
  const prevPathnameRef = useRef(null);
  const nationalInputRef = useRef(null);
  const pendingCaretDigitsRef = useRef(null);

  const isSignIn = location.pathname === '/signin';

  // Clear SSO error when user navigates from signup to signin or vice versa
  useEffect(() => {
    const pathname = location.pathname;
    if (prevPathnameRef.current !== null && prevPathnameRef.current !== pathname) {
      setSsoError(null);
    }
    prevPathnameRef.current = pathname;
  }, [location.pathname]);

  // Read SSO error from URL after redirect from Google/Apple, show in UI, clean URL.
  // Depends on location.search (primitive) to avoid loops from searchParams reference changes
  // and to ensure URL cleanup runs reliably in production.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    if (!errorParam) return;

    setSsoError(getSsoErrorMessage(errorParam, t));

    params.delete('error');
    const nextSearch = params.toString();
    setSearchParams(nextSearch ? `?${nextSearch}` : {}, { replace: true });
  }, [location.search, setSearchParams, t]);

  // DOM-based SSO error visibility: hide .signup-sso-error after 10 seconds (avoids React state timing issues in production).
  useEffect(() => {
    const errorElement = document.querySelector('.signup-sso-error');
    if (!errorElement) return;

    const timer = setTimeout(() => {
      errorElement.style.display = 'none';
    }, 10000);

    return () => clearTimeout(timer);
  }, [ssoError]);

  useEffect(() => {
    if (step !== STEPS.OTP || otpResendTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setOtpResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [step, otpResendTimer]);

  const digitsOnly = (str) => String(str || '').replace(/\D/g, '');
  const sliceDigits = (d, start, len) => d.slice(start, len == null ? undefined : start + len);
  const formatUS = (d) => {
    const a = sliceDigits(d, 0, 3);
    const b = sliceDigits(d, 3, 3);
    const c = sliceDigits(d, 6, 4);
    if (!b) return a;
    if (!c) return `(${a}) ${b}`;
    return `(${a}) ${b}-${c}`;
  };
  const formatBR = (d) => {
    const area = sliceDigits(d, 0, 2);
    const mid = sliceDigits(d, 2, 5);
    const last = sliceDigits(d, 7, 4);
    if (!mid) return area;
    if (!last) return `(${area}) ${mid}`;
    return `(${area}) ${mid}-${last}`;
  };
  const formatNationalNumber = (digits, dialCode) => {
    const clean = digitsOnly(digits);
    if (!clean) return '';
    const dial = String(digitsOnly(dialCode) || '');

    // Country-specific display formats (non-space separators)
    if (dial === '1') return formatUS(clean);
    if (dial === '55') return formatBR(clean);

    const groups = PHONE_GROUP_RULES[dial] || [3, 3, 3];
    const out = [];
    let idx = 0;
    for (const size of groups) {
      if (idx >= clean.length) break;
      out.push(clean.slice(idx, idx + size));
      idx += size;
    }
    if (idx < clean.length) out.push(clean.slice(idx));
    return out.join(' ');
  };
  const caretPosFromDigitsCount = (formatted, digitsCount) => {
    if (!formatted) return 0;
    if (!digitsCount || digitsCount <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < formatted.length; i += 1) {
      if (/\d/.test(formatted[i])) seen += 1;
      if (seen >= digitsCount) return i + 1;
    }
    return formatted.length;
  };
  const normalizeNationalNumber = (rawValue, dialCode) => {
    const raw = String(rawValue || '').trim();
    const dial = digitsOnly(dialCode);
    let digits = digitsOnly(raw);
    if (!digits) return '';

    const hasIntlPrefix = raw.startsWith('+') || raw.startsWith('00') || (dial && digits.startsWith(`00${dial}`));
    if (dial && hasIntlPrefix) {
      if (digits.startsWith(`00${dial}`)) {
        digits = digits.slice(dial.length + 2);
      } else if (digits.startsWith(dial)) {
        digits = digits.slice(dial.length);
      }
    }
    return digits;
  };
  const getPhoneLengthRule = (dialCode) => {
    const cleanDial = digitsOnly(dialCode);
    if (PHONE_LENGTH_RULES[cleanDial]) return PHONE_LENGTH_RULES[cleanDial];
    if (MIDDLE_EAST_DIAL_CODES.has(cleanDial)) return { min: 8, max: 9 };
    if (LATAM_DIAL_CODES.has(cleanDial)) return { min: 9, max: 11 };
    return { min: 8, max: 11 };
  };
  const fullPhone = countryCode && nationalNumber
    ? `+${countryCode}${digitsOnly(nationalNumber)}`
    : '';
  const nationalDigits = digitsOnly(nationalNumber);
  const { min: phoneMin, max: phoneMax } = getPhoneLengthRule(countryCode);
  const phoneValid = nationalDigits.length >= phoneMin && nationalDigits.length <= phoneMax;
  const formattedNationalNumber = formatNationalNumber(nationalNumber, countryCode);

  const handleContinueFromMobile = async () => {
    if (!phoneValid) {
      setPhoneValidationError(true);
      setError(t('signup.errors.invalidPhone'));
      return;
    }
    setPhoneValidationError(false);
    // Channel-selection screen is disabled; always send OTP via SMS.
    setError(null);
    setChannel('sms');

    await handleContinueFromChannel();
  };

  const focusPhoneField = () => {
    const target = nationalInputRef.current;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      target.focus({ preventScroll: true });
    }, 200);
  };

  const handleDisabledContinueAttempt = () => {
    if (loading || phoneValid) return;
    setPhoneValidationError(true);
    setError(t('signup.errors.invalidPhone'));
    focusPhoneField();
  };

  const handleNationalNumberChange = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    pendingCaretDigitsRef.current = digitsOnly(raw.slice(0, caret)).length;

    const digits = normalizeNationalNumber(raw, countryCode);
    const clampedDigits = Number.isFinite(phoneMax) ? digits.slice(0, phoneMax) : digits;
    setNationalNumber(clampedDigits);
    if (phoneValidationError) setPhoneValidationError(false);
    if (error === t('signup.errors.invalidPhone')) setError(null);
  };
  const handleNationalNumberKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!phoneValid || loading) return;
    handleContinueFromMobile();
  };

  const handleChannelSelect = (ch) => {
    setChannel(ch);
  };
  const handleOtpKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (loading) return;
    handleVerifyOtp();
  };

  useLayoutEffect(() => {
    const input = nationalInputRef.current;
    const desiredDigits = pendingCaretDigitsRef.current;
    if (!input || desiredDigits === null) return;
    const nextPos = caretPosFromDigitsCount(formattedNationalNumber, desiredDigits);
    try {
      input.setSelectionRange(nextPos, nextPos);
    } catch {
      // ignore
    } finally {
      pendingCaretDigitsRef.current = null;
    }
  }, [formattedNationalNumber]);

  const intent = isSignIn ? 'login' : 'signup';
  const browserLanguage = (() => {
    try {
      const raw = (
        i18n?.resolvedLanguage ||
        i18n?.language ||
        navigator?.language ||
        navigator?.languages?.[0] ||
        ''
      ).trim();
      if (!raw) return 'en';
      const base = raw.toLowerCase().split('-')[0].split('_')[0];
      return ['es', 'en', 'fr', 'ar', 'zh'].includes(base) ? base : 'en';
    } catch {
      return 'en';
    }
  })();

  /** POST /auth/phone/verify-otp: JWT + `needs_personal_details` → register, success (/es/regularizacion/success), or fallback. */
  const handleOtpSuccess = (response) => {
    const {
      token,
      user,
      needs_personal_details: needsPersonalDetails,
      is_first_applicant_login: isFirstApplicantLogin,
    } = response;
    if (!token) {
      setError(t('signup.errors.invalidResponse'));
      return;
    }
    localStorage.setItem('authToken', token);
    markApplicantSessionStarted();
    const nextIntent = isSignIn
      ? (isFirstApplicantLogin === true ? 'complete_profile' : 'login')
      : 'signup';
    sessionStorage.setItem(APPLICANT_ENTRY_INTENT_KEY, nextIntent);
    if (user) localStorage.setItem('userData', JSON.stringify(user));

    const role = user?.role?.value ?? user?.role;
    if (role === 'admin') {
      navigate('/advisor');
      return;
    }
    if (needsPersonalDetails === true) {
      navigate('/register');
      return;
    }
    if (needsPersonalDetails === false) {
      navigate(
        isSignIn
          ? (isFirstApplicantLogin === true ? '/intake' : '/organizer')
          : '/es/regularizacion/success'
      );
      return;
    }
    navigate(
      isSignIn
        ? (isFirstApplicantLogin === true ? '/intake' : '/organizer')
        : '/es/regularizacion/success'
    );
  };

  const handleVerifyOtp = async () => {
    const code = (otpCode || '').trim();
    if (!code) {
      setError(t('signup.errors.enterCode'));
      return;
    }
    setError(null);
    setLoading(true);
    const partnerToken = getStoredPartnerToken() || (refParam ? refParam : null);
    const referralCode = getStoredReferralCode();
    try {
      const res = await mvpApi.verifyPhoneOtp(
        fullPhone,
        code,
        intent,
        referralCode,
        partnerToken,
        browserLanguage
      );
      handleOtpSuccess(res.data || {});
    } catch (err) {
      if (!isSignIn && err?.response?.status === 409) {
        goToThankYouForExistingUser();
      } else {
        setError(normalizeApiError(err, 'signup.errors.invalidOtp', t));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinueFromChannel = async () => {
    setError(null);
    setLoading(true);

    if (!isSignIn) {
      try {
        const lookupRes = await mvpApi.getUsersByPhone(fullPhone);
        const data = lookupRes?.data || {};
        const total =
          typeof data.total === 'number'
            ? data.total
            : Array.isArray(data.users)
              ? data.users.length
              : 0;
        if (total > 0) {
          setLoading(false);
          goToThankYouForExistingUser();
          return;
        }
      } catch (lookupErr) {
        setError(normalizeApiError(lookupErr, 'signup.errors.sendFailed', t));
        setLoading(false);
        return;
      }
    }

    // Navigate to OTP screen while we request/send the code.
    setOtpResendTimer(30);
    setStep(STEPS.OTP);

    try {
      await mvpApi.requestPhoneOtp(fullPhone, 'sms', intent);
    } catch (err) {
      if (isSignIn && err?.response?.status === 404) {
        setError(t('signup.errors.phoneLoginNotRegistered'));
      } else {
        setError(normalizeApiError(err, 'signup.errors.sendFailed', t));
      }
      setOtpResendTimer(0);
      setStep(STEPS.MOBILE);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResendTimer > 0 || loading) return;
    setError(null);
    setOtpCode('');
    setOtpResendTimer(30);
    setLoading(true);
    try {
      await mvpApi.requestPhoneOtp(fullPhone, 'sms', intent);
    } catch (err) {
      if (isSignIn && err?.response?.status === 404) {
        setError(t('signup.errors.phoneLoginNotRegistered'));
      } else {
        setError(normalizeApiError(err, 'signup.errors.sendFailed', t));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const partnerToken = getStoredPartnerToken() || (refParam ? refParam : null);
    const referralCode = getStoredReferralCode();
    if (partnerToken) setStoredPartnerToken(partnerToken);
    window.location.href = getGoogleAuthUrl(intent, partnerToken, referralCode);
  };
  const handleApple = () => {
    const partnerToken = getStoredPartnerToken() || (refParam ? refParam : null);
    const referralCode = getStoredReferralCode();
    if (partnerToken) setStoredPartnerToken(partnerToken);
    window.location.href = getAppleAuthUrl(intent, partnerToken, referralCode);
  };

  return (
    <div className="signup-page">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="signup-main">
        {/* Top bar: back button + page title (commented out as requested) */}
        <div className="signup-topbar">
          {/*
          <button
            type="button"
            className="signup-back-btn"
            onClick={() => {
              if (step === STEPS.MOBILE) {
                navigate(isSignIn ? '/signup' + (location.search || '') : getPathWithRef('/'));
                return;
              }
              // Skip the disabled CHANNEL step when going back from OTP.
              if (step === STEPS.OTP) {
                setStep(STEPS.MOBILE);
                return;
              }
              setStep(step - 1);
            }}
            aria-label="Go back"
          >
            ←
          </button>
          <span className="signup-topbar-label">{isSignIn ? t('signup.signIn') : t('signup.newAccount')}</span>
          */}
        </div>

        {/* Step indicator pills (two steps: mobile → otp) */}
        <div className="signup-step-pills">
          <div className={`signup-step-pill ${step === STEPS.MOBILE ? 'active' : step === STEPS.OTP ? 'done' : ''}`} />
          <div className={`signup-step-pill ${step === STEPS.OTP ? 'active' : ''}`} />
        </div>

        {step === STEPS.MOBILE && (
          <>
            <h2 className="signup-heading">
              {isSignIn ? (
                <Trans i18nKey="signup.mobileTitleSignIn" components={{ em: <em /> }} />
              ) : (
                <Trans i18nKey="signup.mobileTitle" components={{ em: <em />, br: <br /> }} />
              )}
            </h2>
            <p className="signup-desc">
              <Trans
                i18nKey="signup.mobileDesc"
                components={{
                  privacy: <button type="button" className="signup-inline-link" onClick={() => setOpenLegal('privacy')} />,
                }}
              />
            </p>
            <div className="signup-phone-row">
              <div className="signup-country-wrap">
                <PhoneInput
                  country="es"
                  enableSearch
                  disableSearchIcon
                  value={countryCode}
                  onChange={(_, countryData) => {
                    setCountryCode(countryData?.dialCode || '');
                    setNationalNumber('');
                  }}
                  inputProps={{
                    id: 'countryCode',
                    name: 'countryCode',
                    readOnly: true,
                    'aria-label': 'Country code',
                  }}
                  buttonClass="signup-country-button"
                  containerClass="signup-country-phone"
                  inputClass="signup-country-code-input"
                  dropdownClass="signup-country-dropdown"
                  searchClass="signup-country-search"
                />
              </div>
              <input
                ref={nationalInputRef}
                type="tel"
                className={`signup-national-input ${phoneValidationError && !phoneValid ? 'signup-national-input--invalid' : ''}`}
                placeholder=""
                value={formattedNationalNumber}
                onChange={handleNationalNumberChange}
                onKeyDown={handleNationalNumberKeyDown}
                inputMode="numeric"
                autoComplete="tel-national"
                aria-label="Phone number"
              />
            </div>
            {ssoError && (
              <p className="signup-sso-error" role="alert">
                {ssoError}
              </p>
            )}
            {error && <p className="signup-error">{error}</p>}
            <div className="signup-btn-primary-wrap" onClick={handleDisabledContinueAttempt}>
              <button
                type="button"
                className="signup-btn-primary"
                onClick={handleContinueFromMobile}
                disabled={!phoneValid || loading}
              >
                {t('signup.continue')}
              </button>
            </div>
            <div className="signup-or-divider">
              <span className="signup-or-line" />
              <span className="signup-or-text">{t('signup.or')}</span>
              <span className="signup-or-line" />
            </div>
            <div className="signup-social-row">
              <button type="button" className="signup-btn-social" onClick={handleGoogle}>
                <GoogleIcon />
                Google
              </button>
              <button type="button" className="signup-btn-social" onClick={handleApple}>
                <AppleIcon />
                Apple
              </button>
            </div>
          </>
        )}

        {/* CHANNEL step is disabled for now (OTP is requested immediately after phone number submission).
            Keeping this UI commented makes it easy to restore later. */}
        {false && (
          <>
            <h2 className="signup-heading"><Trans i18nKey="signup.secondTitle" components={{ em: <em /> }} /></h2>
            <p className="signup-desc">{t('signup.secondDesc')}</p>
            <div className="signup-channel-cards">
              {/* WhatsApp channel card — restore with useState('whatsapp') default when re-enabling
              <button
                type="button"
                className={`signup-channel-card ${channel === 'whatsapp' ? 'selected' : ''}`}
                onClick={() => handleChannelSelect('whatsapp')}
              >
                <span className="signup-channel-icon">💬</span>
                <div className="signup-channel-text">
                  <strong>WhatsApp</strong>
                  <span>{t('signup.whatsappDesc')}</span>
                </div>
                <span className="signup-channel-radio" />
              </button>
              */}
              <button
                type="button"
                className={`signup-channel-card ${channel === 'sms' ? 'selected' : ''}`}
                onClick={() => handleChannelSelect('sms')}
              >
                <span className="signup-channel-icon">📱</span>
                <div className="signup-channel-text">
                  <strong>{t('signup.smsTitle')}</strong>
                  <span>{t('signup.smsDesc')}</span>
                </div>
                <span className="signup-channel-radio" />
              </button>
            </div>
            <div className="signup-channel-tip-card" role="status">
              <span className="signup-channel-tip-icon" aria-hidden>
                <LightbulbIcon />
              </span>
              <p className="signup-channel-tip-text">
                {t('signup.tip')}
              </p>
            </div>
            <button type="button" className="signup-btn-primary" onClick={handleContinueFromChannel} disabled={loading}>
              {loading ? t('signup.sending') : t('signup.continue')}
            </button>
          </>
        )}

        {step === STEPS.OTP && (
          <>
            <h2 className="signup-heading">{t('signup.otpTitle')}</h2>
            <p className="signup-desc">{t('signup.otpDesc', { channel: channel === 'whatsapp' ? 'WhatsApp' : t('fields.telefono') })}</p>
            <input
              type="text"
              className="signup-otp-input"
              placeholder={t('signup.otpPlaceholder')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={handleOtpKeyDown}
              maxLength={6}
              autoComplete="one-time-code"
            />
            <button type="button" className="signup-btn-primary" onClick={handleVerifyOtp} disabled={loading}>
              {loading ? t('signup.verifying') : t('signup.continue')}
            </button>
            <p className="signup-otp-resend-timer">
              {otpResendTimer > 0 ? (
                <>
                  <span>{t('signup.otpResendTimerPrefix')}</span>
                  <span className="notranslate" translate="no">
                    {otpResendTimer}s
                  </span>
                </>
              ) : (
                t('signup.otpResendReady')
              )}
            </p>
            {otpResendTimer <= 0 && (
              <button
                type="button"
                className="signup-btn-resend"
                onClick={handleResendOtp}
                disabled={loading}
              >
                {loading ? t('signup.sending') : t('signup.otpResendCode')}
              </button>
            )}
          </>
        )}

        {error && step !== STEPS.MOBILE && <p className="signup-error">{error}</p>}

        <p className="signup-terms">
          <Trans
            i18nKey="signup.terms"
            components={{
              terms: <button type="button" onClick={() => setOpenLegal('terms')}>Terms of Service</button>,
              privacy: <button type="button" onClick={() => setOpenLegal('privacy')}>Privacy Policy</button>,
              dpa: <button type="button" onClick={() => setOpenLegal('data')}>Data Processing Agreement</button>,
            }}
          />
        </p>
        {isSignIn ? (
          <p className="signup-signin-row">
            <>{t('signup.noAccount')} <Link to={`/signup${location.search || ''}`}>{t('signup.signUpCta')}</Link></>
          </p>
        ) : null}
      </main>
      <LegalModal type={openLegal || 'terms'} open={openLegal !== null} onClose={() => setOpenLegal(null)} />
    </div>
  );
}
